/* ==========================================================================
   Carousel Controller — <carousel-component>
   Powered by Swiper.js (lazy loaded via CDN).
   Hydration tier: 'visible' — o bundle do Swiper só baixa quando o
   carrossel entra na viewport. Emite 'carousel:init' com detail.swiper.
   ========================================================================== */

import { BaseComponent } from '@theme/base-component';

class CarouselComponent extends BaseComponent {
  static hydration = 'visible';

  #resizeObserver = null;

  setup() {
    this.swiper = null;
    this.options = {};
    this.#initialize();
  }

  async #initialize() {
    await this.#loadSwiper();
    if (!this.isConnected) return;
    this.#initSwiper();

    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.swiper && typeof this.swiper.update === 'function') {
          this.swiper.update();
        }
      });
      this.#resizeObserver.observe(this);
    }
  }

  teardown() {
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.classList.remove('is-fade-ready', 'has-fade-start', 'has-fade-end');
  }

  /* Fade lateral: cada ponta só desbota quando ainda esconde conteúdo daquele
     lado. O CSS cuida da geometria (snippets/carousel.liquid). */
  #syncFade = () => {
    const sw = this.swiper;
    if (!sw) return;

    // Nada para rolar (ex.: 3 colunas configuradas, 2 slides publicados):
    // nenhuma das pontas esconde conteúdo.
    const idle = sw.isBeginning && sw.isEnd;
    const loop = Boolean(sw.params.loop);

    this.classList.toggle('has-fade-start', !idle && (loop || !sw.isBeginning));
    this.classList.toggle('has-fade-end', !idle && (loop || !sw.isEnd));
  };

  async #loadSwiper() {
    if (window.Swiper) return;
    if (window.SwiperLoading) return window.SwiperLoading;

    window.SwiperLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';

      const style = document.createElement('link');
      style.rel = 'stylesheet';
      style.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

      script.onload = () => resolve();
      script.onerror = reject;

      document.head.appendChild(style);
      document.head.appendChild(script);
    });

    return window.SwiperLoading;
  }

  #initSwiper() {
    const container = this.querySelector('.swiper');
    if (!container) return;

    const rawOptions = this.getAttribute('data-swiper-options');
    if (rawOptions) {
      try {
        this.options = JSON.parse(rawOptions);
      } catch (e) {
        console.error('Invalid Swiper options JSON:', e);
      }
    }

    const gap = parseInt(this.getAttribute('data-gap')) || 0;
    const deskItems = parseFloat(this.getAttribute('data-desktop-items')) || 3;
    const mobileItems = parseFloat(this.getAttribute('data-mobile-items')) || 1;

    const defaultOptions = {
      slidesPerView: mobileItems,
      spaceBetween: gap,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true,
      },
      loop: this.hasAttribute('data-loop') && this.getAttribute('data-loop') !== 'false',
      autoplay: this.hasAttribute('data-autoplay') ? {
        delay: parseInt(this.getAttribute('data-autoplay-delay')) || 3000,
        disableOnInteraction: false,
      } : false,
      freeMode: this.hasAttribute('data-free-mode'),
      breakpoints: this.options.breakpoints || {
        768: { slidesPerView: deskItems },
      },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Slide Anterior',
        nextSlideMessage: 'Próximo Slide',
      },
    };

    const finalOptions = { ...defaultOptions, ...this.options };

    this.swiper = new window.Swiper(container, finalOptions);

    if (this.classList.contains('carousel--fade')) {
      this.classList.add('is-fade-ready');
      this.#syncFade();
      this.swiper.on('progress', this.#syncFade);
      this.swiper.on('resize', this.#syncFade);
      this.swiper.on('slidesLengthChange', this.#syncFade);
      this.swiper.on('update', this.#syncFade);
    }

    this.emit('carousel:init', { swiper: this.swiper });
  }

  slideNext() { if (this.swiper) this.swiper.slideNext(); }
  slidePrev() { if (this.swiper) this.swiper.slidePrev(); }
  slideTo(index) { if (this.swiper) this.swiper.slideTo(index); }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}
